import { $, hideOverlay, showOverlay } from "@jpj/game-shell";
import { solvePacking3D } from "@jpjj/solver-core";
import type { Pack3DItem, Pack3DResult } from "@jpjj/solver-core/types";
import type { Stage, LootPiece } from "../three/scene";
import { ISLANDS } from "../game/levels";
import { LOOT } from "../game/items";
import { state } from "../game/state";

/**
 * The signature moment: for every island, a REAL HiGHS solve runs live in a
 * Web Worker, then the proven-best deck is rebuilt piece by piece on the
 * actual 3D ship while the player's own deck waits one toggle away. All
 * numbers shown come from that live solve — never from the stored level data.
 */

let stage: Stage;
export function initReveal(s: Stage): void {
  stage = s;
}

function islandItems(k: number): Pack3DItem[] {
  return ISLANDS[k].loot.map((key) => {
    const t = LOOT[key];
    return { key, value: t.value, w: t.w, d: t.d, h: t.h, stackable: t.stackable };
  });
}

const solvePromises: Array<Promise<Pack3DResult> | null> = ISLANDS.map(() => null);
const solveResults: Array<Pack3DResult | null> = ISLANDS.map(() => null);

function solveIsland(k: number): Promise<Pack3DResult> {
  let pr = solvePromises[k];
  if (!pr) {
    const isl = ISLANDS[k];
    pr = solvePacking3D({
      cols: isl.cols, rows: isl.rows, maxH: isl.maxH,
      items: islandItems(k), timeLimitSec: 25,
    }).then((res) => { solveResults[k] = res; return res; });
    solvePromises[k] = pr;
  }
  return pr;
}

/** Kick off every island's solve (called once the reveal starts). */
export function prepareSolves(): Promise<Pack3DResult[]> {
  return Promise.all(ISLANDS.map((_, k) => solveIsland(k)));
}

/* ---------------- the comparison, island by island ---------------- */

let cmpIndex = 0;
let animToken = 0;
const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

const byBuildOrder = (a: LootPiece, b: LootPiece) => a.z - b.z || a.y - b.y || a.x - b.x;

function recPieces(k: number): LootPiece[] {
  return (state.records[k]?.placements ?? []).map((p) => ({ ...p })).sort(byBuildOrder);
}
function optPieces(k: number): LootPiece[] {
  const res = solveResults[k];
  if (!res) return [];
  return res.placements
    .map((p, i) => ({ uid: -1000 - i, key: p.key, x: p.x, y: p.y, z: p.z, w: p.w, d: p.d, h: p.h }))
    .sort(byBuildOrder);
}

function showInstant(pieces: LootPiece[]): void {
  stage.clearLoot();
  for (const p of pieces) stage.addLoot(p, false);
}

function setActiveView(which: "you" | "opt" | null): void {
  $("viewYouBtn").classList.toggle("active", which === "you");
  $("viewOptBtn").classList.toggle("active", which === "opt");
}

export function openReveal(): void {
  state.phase = "reveal";
  document.body.classList.add("reveal-on");
  hideOverlay("islandOverlay");
  showOverlay("solverOverlay");
  stage.setRevealMode(true);
  stage.setGhost(null);
  void prepareSolves();
  cmpIndex = 0;
  void runIsland(0);
}

async function runIsland(k: number): Promise<void> {
  const token = ++animToken;
  cmpIndex = k;
  const isl = ISLANDS[k];
  const rec = state.records[k];
  const youVal = rec?.value ?? 0;

  $("cmpHead").innerHTML = `Island ${k + 1} / ${ISLANDS.length} — <span class="lv-mon">${isl.name}</span>`;
  $("youScore").textContent = String(youVal);
  $("optScore").textContent = "—";
  $("cmpGap").innerHTML = "&nbsp;";
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  ($("viewYouBtn") as HTMLButtonElement).disabled = true;
  ($("viewOptBtn") as HTMLButtonElement).disabled = true;
  setActiveView("you");

  stage.setIsland(isl.cols, isl.rows, isl.maxH);
  showInstant(recPieces(k));

  const status = $("solverStatus");
  status.textContent = `HiGHS is loading the quay manifest… searching every legal spot`;
  let res: Pack3DResult;
  try {
    res = await solveIsland(k);
  } catch {
    status.textContent = "The solver could not start — reload and try again.";
    return;
  }
  if (token !== animToken) return;

  const proven = res.status === "OPTIMAL";
  status.textContent = proven
    ? `HiGHS proved the optimum in ${Math.round(res.tSolveMs)} ms`
    : `HiGHS best found in ${Math.round(res.tSolveMs)} ms — time limit hit, not a proof`;
  $("optScore").previousElementSibling!.textContent = proven ? "Proven best" : "Best found";

  await sleep(900);
  if (token !== animToken) return;

  // the optimizer loads its deck, piece by piece
  stage.clearLoot();
  setActiveView("opt");
  const pieces = optPieces(k);
  let run = 0;
  for (const p of pieces) {
    if (token !== animToken) return;
    stage.addLoot(p, true);
    run += LOOT[p.key].value;
    $("optScore").textContent = String(run);
    await sleep(170);
  }
  $("optScore").textContent = String(res.value);

  const gap = res.value - youVal;
  $("cmpGap").textContent = gap <= 0
    ? "You matched it — this deck could not be loaded better."
    : `${gap} more gold fit on this deck (you reached ${res.value > 0 ? Math.round((youVal / res.value) * 100) : 0}% of ${proven ? "the proven optimum" : "the best found"}).`;

  ($("cmpNext") as HTMLButtonElement).disabled = false;
  ($("viewYouBtn") as HTMLButtonElement).disabled = false;
  ($("viewOptBtn") as HTMLButtonElement).disabled = false;
  $("cmpNext").textContent = k === ISLANDS.length - 1 ? "See your raid ▸" : "Next island ▸";
}

export function viewYou(): void {
  animToken++;
  showInstant(recPieces(cmpIndex));
  setActiveView("you");
  const rec = state.records[cmpIndex];
  $("optScore").textContent = String(solveResults[cmpIndex]?.value ?? "—");
  $("youScore").textContent = String(rec?.value ?? 0);
}

export function viewOpt(): void {
  animToken++;
  showInstant(optPieces(cmpIndex));
  setActiveView("opt");
  $("optScore").textContent = String(solveResults[cmpIndex]?.value ?? "—");
}

export function nextCompare(): void {
  if (cmpIndex < ISLANDS.length - 1) {
    void runIsland(cmpIndex + 1);
  } else {
    showFinal();
  }
}

/* ---------------- the run summary ---------------- */

function showFinal(): void {
  state.phase = "final";
  animToken++;
  hideOverlay("solverOverlay");
  stage.setRevealMode(false);

  const youTotal = state.records.reduce((s, r) => s + (r?.value ?? 0), 0);
  const optTotal = solveResults.reduce((s, r) => s + (r?.value ?? 0), 0);
  const allProven = solveResults.every((r) => r?.status === "OPTIMAL");
  $("fcYou").textContent = String(youTotal);
  $("fcOpt").textContent = String(optTotal);
  $("fcSub").textContent = allProven ? "gold · proven optimal" : "gold · best found";
  const gap = optTotal - youTotal;
  $("fcGap").textContent = gap <= 0
    ? "A flawless raid — across all five islands, not one more coin would fit."
    : `Across five islands you shipped ${youTotal} gold. The optimizer shipped ${optTotal} — ` +
      `${gap} more, with the same loot, the same deck, the same rules (you reached ` +
      `${optTotal > 0 ? Math.round((youTotal / optTotal) * 100) : 0}%).`;

  const rows = state.records.map((rec, k) => {
    const o = solveResults[k];
    const ov = o?.value ?? 0;
    const v = rec?.value ?? 0;
    const stars = "★".repeat(rec?.stars ?? 0) + "☆".repeat(3 - (rec?.stars ?? 0));
    return `<tr><td>${k + 1} · ${ISLANDS[k].name}</td><td>${v}</td><td class="gold">${ov}</td>` +
      `<td>${ov > 0 ? Math.round((v / ov) * 100) : 0}%</td><td class="stars-cell">${stars}</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Island</th><th>You</th><th>Optimal</th><th>%</th><th></th></tr></thead><tbody>${rows}</tbody>`;

  showOverlay("endOverlay");
}

/** Reset the reveal UI between runs (solves are cached — the loot is fixed). */
export function resetReveal(): void {
  animToken++;
  cmpIndex = 0;
}
